import React, { useRef, useEffect } from "react";
import { useSectionInView } from "../assets/lib/hooks";
import { useTheme } from "../context/theme-context";
import RadialGradient from "./RadialGradient";
import { motion, useScroll, useTransform } from "framer-motion";
import * as d3 from "d3";
import { nest } from 'd3-collection';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import { Outlet, Link } from "react-router-dom";

const SkillsHierarchy: React.FC = () => {
  const skillHierarchyStatus = useTypedSelector(state => state.SkillHierarchy.loading)
  const skillHierarchyData = useTypedSelector(state => state.SkillHierarchy.data)

  // const { ref } = useSectionInView("Skills");
  // const { theme } = useTheme();
  // const { language } = useLanguage();
  // const animationReference = useRef<HTMLDivElement>(null);
  // const { scrollYProgress } = useScroll({
  //   target: animationReference,
  //   offset: ["0 1", "1.33 1"],
  // });
  // const scaleProgess = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  // const opacityProgess = useTransform(scrollYProgress, [0, 1], [0.6, 1]);

  // const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // let screenWidth = [600, 800, 1000, 1200, 1500, 1800]

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', function (event) {
      handleResize()
    });
  }, [skillHierarchyStatus, skillHierarchyData]);

  function handleResize() {
    if (!skillHierarchyStatus) {
      draw(window.innerHeight, window.document.body.clientWidth)
    }
  }

  function draw(clientHeight: number, clientWidth: number) {

    function fontSize(width : number) {
      let size = ""
      switch (true) {
        case (clientWidth < 600):
          size = "11px"
          break;
        case (clientWidth < 800):
          size = "12px"
          break;
        case (clientWidth < 1000):
          size = "13px"
          break;
        case (clientWidth < 1200):
          size = "14px"
          break;
        case (clientWidth < 1500):
          size = "16px"
          break;
        case (clientWidth < 1800):
          size = "18px"
          break;
        default:
          size = "20px"
          break;
      }
      return size
    }

    function measurement(dimension: number, percent: number) {
      return (dimension - (dimension * percent / 100))
    }
    
    clientHeight = measurement(clientHeight, 26)
    clientWidth = measurement(clientWidth, 10)
    
    const color = d3.scaleOrdinal(d3.quantize(d3.interpolateRainbow, skillHierarchyData.children.length + 1));

    const hierarchy: any = d3.hierarchy(skillHierarchyData)
      .sum((d: any) => d.value)
      .sort((a: any, b: any) => b.height - a.height || b.value - a.value);

    const root = d3.partition().size([clientHeight, clientWidth])(hierarchy);

    d3.selectAll("#graphicalSkillsDiv").remove()

    let svg = d3.select("#graphicalSkills")
      .append("div")
      .attr("id", "graphicalSkillsDiv")
      .append("svg")
      .attr("preserveAspectRatio", "xMinYMin meet")
      .classed("svg-content-responsive", true)
      .attr("viewBox", "0 0 " + clientWidth + " " + clientHeight);

    const cell = svg
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("transform", (d: any) => `translate(${d.y0},${d.x0})`);

    const rect = cell.append("rect")
      .attr("width", d => d.y1 - d.y0 - 1)
      .attr("height", d => rectHeight(d))
      .attr("fill-opacity", 0.6)
      .attr("fill", (d: any) => {
        if (!d.depth) return "rgb(35, 171, 216)";
        while (d.depth > 1) d = d.parent;
        return color(d.data.name);
      })
      .style("cursor", "pointer")
      .on("click", clicked);

    const nameText = cell.append("text")
      .style("user-select", "none")
      .attr("pointer-events", "none")
      .attr("x", 4)
      .attr("y", 15)
      .attr("fill-opacity", d => +labelVisible(d))
      .text((d: any) => d.data.name)
      .attr("font-family", "poppins-regular")
      .attr("font-size", fontSize(clientWidth));

    cell.append("title").text((d: any) => `${d.ancestors().map((d: any) => d.data.name).reverse().join(" / ")}`);

    let focus: any = root;
    let selectedName = "";
    function clicked(event: any, p: any) {
      focus = focus === p ? p = p.parent : p;
      if (focus) {
        d3.selectAll("#selectedDetails").remove()
        if (focus.depth == 2) {
          let selected: any = focus.data
          const selectedDetails = svg.append("foreignObject")
            .attr("id", "selectedDetails")
            .attr("x", ((clientWidth) / 3) + 10)
            .attr("y", 0)
            .attr("width", (clientWidth - ((clientWidth) / 3) - 10))
            .attr("height", clientHeight)
            .append("xhtml:div")
            .style("font",  fontSize(clientWidth) + " 'poppins-regular'")
            .html((selected.firstYearUsed ? "<b>The years of experience</b> : " + selected.firstYearUsed + " ~ " + selected.lastYearUsed + "</br>" : "") +
              (selected.versions.length > 0 ? "<b>The versions</b> : " + selected.versions + "</br>" : "") +
              (selected.description ? "<b>What is this</b> : " + selected.description + "</br>" : "") +
              (selected.whyIUsed ? "<b>What I have done</b> : " + selected.whyIUsed + "</br>" : ""));
        }
        root.each((d: any) => d.target = {
          x0: (d.x0 - p.x0) / (p.x1 - p.x0) * clientHeight,
          x1: (d.x1 - p.x0) / (p.x1 - p.x0) * clientHeight,
          y0: d.y0 - p.y0,
          y1: d.y1 - p.y0
        });
        const t = cell.transition().duration(750).attr("transform", (d: any) => `translate(${d.target.y0},${d.target.x0})`);
        rect.transition(t).attr("height", (d: any) => rectHeight(d.target));
        nameText.transition(t).attr("fill-opacity", (d: any) => +labelVisible(d.target));
      }
    }

    function rectHeight(d: any) {
      return d.x1 - d.x0 - 1;
    }

    function labelVisible(d: any) {
      return d.y1 <= clientWidth && d.y0 >= 0 && d.x1 - d.x0 > 16;
    }
  }

  const linkStyle = {
    color: '#0000ff82'
  };

  return (
    <React.Fragment>
      <section className="relative tech-stack p-3" id="hierarchy" >
        <div className="title-container flex flex-col justify-center p-32 gap-6 p-0  w-full items-start py-16">
          <h2>Skills <span className="text-[--orange]">Hierarchy</span></h2>
          <Link to="/#skills" style={linkStyle}>Back</Link>
        </div>
        <div id="graphicalSkills"></div>
      </section>
    </React.Fragment>
  );
};

export default SkillsHierarchy;
